import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  AudioLines,
  CalendarDays,
  Check,
  ChevronDown,
  LayoutDashboard,
  MessageSquare,
  Phone,
  Users,
  Workflow,
} from "lucide-react";
import { Link } from "./Navigation";
import logo from "@/assets/logo-icon.webp";
import { LiquidField } from "./LiquidField";

const modes = [
  { id: "overview", title: "Overview", icon: LayoutDashboard },
  { id: "calls", title: "AI receptionist", icon: AudioLines },
  { id: "crm", title: "CRM", icon: Users },
  { id: "calendar", title: "Calendar", icon: CalendarDays },
] as const;

export function HeroWorkspace({
  method = "shader",
}: {
  method?: "shader" | "css" | "video";
}) {
  const [mode, setMode] = useState("overview");
  return (
    <div className="ch-product">
      <aside className="ch-product-sidebar">
        <div className="ch-product-brand">
          <img src={logo} alt="" />
          <strong>Chleb AI</strong>
          <ChevronDown size={15} />
        </div>
        <div
          className="ch-product-tabs"
          role="group"
          aria-label="Explore the hero product example"
        >
          {modes.map((item) => (
            <button
              key={item.id}
              onClick={() => setMode(item.id)}
              aria-pressed={mode === item.id}
            >
              <item.icon size={18} />
              {item.title}
            </button>
          ))}
        </div>
        <div className="ch-product-sidebar-note">
          <Workflow size={20} />
          <span>
            One connected
            <br />
            front office.
          </span>
        </div>
        <p className="ch-product-example">Interactive product example</p>
      </aside>
      <div className="ch-product-main">
        <LiquidField method={method} />
        <div className="ch-product-toolbar">
          <span>YOUR FRONT OFFICE</span>
          <span>Illustrative workspace</span>
        </div>
        <div className="ch-product-view" data-mode={mode} key={mode}>
          {mode === "overview" ? (
            <>
              <div className="ch-product-symbol">
                <img src={logo} alt="" />
              </div>
              <h2>Ready for the next hello.</h2>
              <p className="ch-product-summary">
                Calls, records and bookings.
                <br />
                Together in your front office.
              </p>
              <Link to="/solutions#ai-receptionist" className="ch-product-cta">
                Try the voice agent <AudioLines size={19} />
              </Link>
              <div className="ch-product-flow">
                <span>
                  <Phone size={16} /> Answer
                </span>
                <ArrowRight size={14} />
                <span>
                  <Users size={16} /> Organize
                </span>
                <ArrowRight size={14} />
                <span>
                  <CalendarDays size={16} /> Book
                </span>
              </div>
              <div className="ch-conversation">
                <span className="ch-conversation-label">A simple example</span>
                <p>“Can I arrange a service visit?”</p>
                <div>
                  <AudioLines size={18} />
                  <span>A call becomes a booked job.</span>
                </div>
              </div>
            </>
          ) : mode === "calls" ? (
            <>
              <div className="ch-product-symbol">
                <AudioLines size={32} />
              </div>
              <h2>
                Your business.
                <br />
                Your welcome.
              </h2>
              <p className="ch-product-summary">
                Your services, hours, and booking rules.
                <br />
                Ready when the next call comes in.
              </p>
              <div className="ch-product-wave" aria-hidden="true">
                {Array.from({ length: 35 }, (_, index) => (
                  <i
                    key={index}
                    style={{
                      height: `${10 + Math.sin(index * 0.7) ** 2 * 31}px`,
                      animationDelay: `${(index % 7) * -0.16}s`,
                    }}
                  />
                ))}
              </div>
              <Link to="/solutions#ai-receptionist" className="ch-product-cta">
                Call the agent <ArrowUpRight size={18} />
              </Link>
            </>
          ) : mode === "crm" ? (
            <>
              <div className="ch-product-symbol">
                <Users size={30} />
              </div>
              <h2>A place for every lead.</h2>
              <p className="ch-product-summary">
                Keep the details. See what comes next.
              </p>
              <div className="ch-pipeline">
                {[
                  ["New inquiry", "Service visit requested", MessageSquare],
                  ["Booked", "Arrange an appointment", CalendarDays],
                  ["Follow-up", "Keep in touch", Workflow],
                ].map(([label, copy, Icon]) => {
                  const I = Icon as typeof Users;
                  return (
                    <div key={String(label)}>
                      <span>{String(label)}</span>
                      <article>
                        <I size={20} />
                        <strong>{String(copy)}</strong>
                      </article>
                    </div>
                  );
                })}
              </div>
              <Link to="/solutions#crm" className="ch-product-cta">
                Explore and buy CRM <ArrowUpRight size={18} />
              </Link>
            </>
          ) : (
            <>
              <div className="ch-product-symbol">
                <CalendarDays size={31} />
              </div>
              <h2>Make room for what’s next.</h2>
              <p className="ch-product-summary">
                A call today.
                <br />
                An appointment in your calendar.
              </p>
              <div className="ch-calendar-example">
                <div>
                  {["M", "T", "W", "T", "F"].map((day, i) => (
                    <span key={i} className={i === 2 ? "selected" : ""}>
                      {day}
                      <b>{i === 2 ? <Check size={15} /> : <i />}</b>
                    </span>
                  ))}
                </div>
                <p>
                  <CalendarDays size={19} />
                  <span>
                    A service visit
                    <br />
                    <small>Connected to the call</small>
                  </span>
                </p>
              </div>
              <Link to="/book" className="ch-product-cta">
                Open our live calendar <ArrowUpRight size={18} />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
